import React, { useState } from 'react';
import { sendPaystubNotifications, getDefaultMessage, validatePhoneNumber, validateEmail, getMessageTemplates } from '../utils/smsService';

const SMSNotificationModal = ({ isOpen, onClose, selectedEmployee, employees, payrollNumber = null }) => {
  const [sending, setSending] = useState(false);
  const [sendAll, setSendAll] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [messageType, setMessageType] = useState('paystub');
  const [notificationType, setNotificationType] = useState('sms'); // 'sms' or 'email'
  const [result, setResult] = useState(null);

  const messageTemplates = getMessageTemplates();

  // Get personalized default message based on selection
  const getPersonalizedMessage = () => {
    if (!sendAll && selectedEmployee) {
      return getDefaultMessage(selectedEmployee.employee_name, messageType);
    }
    return getDefaultMessage(null, messageType);
  };

  const defaultMessage = getPersonalizedMessage();

  const handleSendSMS = async () => {
    setSending(true);
    setResult(null);

    try {
      const recipients = sendAll ? employees : [selectedEmployee];
      console.log('Raw recipients before processing:', recipients);
      console.log('sendAll flag:', sendAll);
      console.log('employees array:', employees);
      console.log('selectedEmployee:', selectedEmployee);
      
      // Create personalized messages for each recipient
      const recipientsWithMessages = recipients.map(emp => {
        const personalizedMessage = customMessage.trim() || getDefaultMessage(emp.employee_name, messageType);
        
        return {
          employee_name: emp.employee_name,
          employee_number: emp.employee_number,
          phone: emp.phone,
          email: emp.email,
          message: personalizedMessage
        };
      });

      // Validate recipients based on notification type
      const validRecipients = recipientsWithMessages.filter(emp => {
        if (notificationType === 'sms') {
          return emp.phone && validatePhoneNumber(emp.phone);
        } else {
          return emp.email && validateEmail(emp.email);
        }
      });
      
      if (validRecipients.length === 0) {
        setResult({
          success: false,
          message: notificationType === 'sms' 
            ? 'No valid phone numbers found for selected employees.' 
            : 'No valid email addresses found for selected employees.'
        });
        setSending(false);
        return;
      }
      
      console.log(`Sending ${notificationType} to recipients:`, validRecipients);
      
      // Call the API with notification type and payroll number for validation
      const apiResult = await sendPaystubNotifications(validRecipients, notificationType, payrollNumber);
      
      setResult({
        success: apiResult.success,
        sent: apiResult.sent || 0,
        failed: apiResult.failed || 0,
        message: apiResult.message || 'SMS notifications processed',
        details: apiResult.results || []
      });

    } catch (error) {
      console.error('Error sending SMS:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      setResult({
        success: false,
        message: `Failed to send SMS notifications: ${error.message}`
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setSendAll(false);
    setCustomMessage('');
    setMessageType('paystub');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8">
        <div className="p-6 max-h-[calc(90vh-4rem)] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Send Notification</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {result ? (
            <div className={`p-4 rounded-lg mb-6 ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <div className="flex items-center">
                <svg className={`w-5 h-5 mr-2 ${result.success ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={result.success ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                </svg>
                <div>
                  <p className="font-medium">{result.message}</p>
                  {result.success && (
                    <p className="text-sm mt-1">
                      Sent: {result.sent} | Failed: {result.failed}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Notification Type Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Notification Method:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    notificationType === 'sms' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="notificationType"
                      checked={notificationType === 'sms'}
                      onChange={() => setNotificationType('sms')}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <div className="text-2xl mb-2">📱</div>
                      <p className="font-medium text-gray-900">SMS</p>
                      <p className="text-xs text-gray-600 mt-1">Text Message</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    notificationType === 'email' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="notificationType"
                      checked={notificationType === 'email'}
                      onChange={() => setNotificationType('email')}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <div className="text-2xl mb-2">✉️</div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-xs text-gray-600 mt-1">Email Message</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Recipients Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Send {notificationType === 'sms' ? 'SMS' : 'Email'} To:
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                    !sendAll ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="recipient"
                      checked={!sendAll}
                      onChange={() => setSendAll(false)}
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Selected Employee</p>
                      {selectedEmployee?.employee_name && (
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedEmployee.employee_name} (#{selectedEmployee.employee_number})
                          {notificationType === 'sms' ? (
                            selectedEmployee.phone ? 
                              <span className="text-green-600 ml-2">✓ {selectedEmployee.phone}</span> : 
                              <span className="text-red-600 ml-2">✗ No phone</span>
                          ) : (
                            selectedEmployee.email ? 
                              <span className="text-green-600 ml-2">✓ {selectedEmployee.email}</span> : 
                              <span className="text-red-600 ml-2">✗ No email</span>
                          )}
                        </p>
                      )}
                    </div>
                  </label>
                  
                  <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                    sendAll ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="recipient"
                      checked={sendAll}
                      onChange={() => setSendAll(true)}
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">All Employees</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {employees.length} total employees
                        {notificationType === 'sms' ? 
                          ` (${employees.filter(emp => emp.phone).length} with phone)` : 
                          ` (${employees.filter(emp => emp.email).length} with email)`
                        }
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Message Type Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Message Type:
                </label>
                <div className="space-y-2">
                  {Object.entries(messageTemplates).map(([key, template]) => (
                    <label key={key} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                      messageType === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="messageType"
                        checked={messageType === key}
                        onChange={() => {
                          setMessageType(key);
                          setCustomMessage(''); // Clear custom message when template changes
                        }}
                        className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        <p className="text-xs text-gray-500 mt-2 italic">
                          "{template.template.substring(0, 80)}..."
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Custom Message (Optional):
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={defaultMessage}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  rows="4"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    {customMessage.trim() ? 'Using custom message' : `Using ${messageTemplates[messageType].name} template`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(customMessage || defaultMessage).length}/160 characters
                  </p>
                </div>
              </div>

              {/* API Status Note */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-green-800">
                    <strong>Production Ready:</strong> This will send real {notificationType === 'sms' ? 'SMS notifications via Dialpad API' : 'email notifications'}. Ensure your backend server is running and configured.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                onClick={handleSendSMS}
                disabled={sending}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors flex items-center gap-2"
              >
                {sending && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {sending ? 'Sending...' : `Send ${notificationType === 'sms' ? 'SMS' : 'Email'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSNotificationModal;